import { Link } from 'react-router';
import {
  Mail,
  Phone,
  MapPin,
  Send,
  Home,
  PlusSquare,
  LayoutDashboard,
  Info,
  ShieldCheck,
  Heart,
  Map as MapIcon,
  Users,
  Briefcase,
  Handshake,
  FileText,
  Lock,
  Clock,
  BadgePercent,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { isNativeApp } from '../../native/platform';
import { FaFacebook, FaGlobe, FaInstagram, FaTiktok } from 'react-icons/fa';
import { SEO_REGIONS, SEO_LANGS, hubPath, type LangCode } from '../data/regions';

// Width-matched copy of the home hero (public/hero/), not the 1920px original.
const FooterImgBG =
  typeof window !== 'undefined' && window.innerWidth < 800
    ? '/hero/bishkek-800.webp'
    : '/hero/bishkek-1280.webp';

const LogoTuraq = new URL('../../assetts/icons/logo-sm.png', import.meta.url)
  .href;

const NAV_LINKS = [
  {
    path: '/listings',
    icon: LayoutDashboard,
    key: 'browse',
    label: 'Жарнамаларды көрүү',
  },
  {
    path: '/specialists',
    icon: Users,
    key: 'specialists',
    label: 'Адистер',
  },
  {
    path: '/create-listing',
    icon: PlusSquare,
    key: 'post',
    label: 'Жарнама жайгаштыруу',
  },
  {
    path: '/dashboard',
    icon: Home,
    key: 'dashboard',
    label: 'Жеке кабинет',
  },
  {
    path: '/favorites',
    icon: Heart,
    key: 'favorites',
    label: 'Тандалгандар',
  },
  {
    path: '/aboutus',
    icon: Info,
    key: 'about',
    label: 'Биз тууралуу',
  },
  {
    path: '/turaq-kg-genplan-bishkek2050',
    icon: MapIcon,
    key: 'genplan',
    label: 'Бишкектин башкы планы 2050',
  },
];

const COMPANY_LINKS = [
  {
    path: '/aboutus',
    icon: Users,
    key: 'about',
    label: 'Компания тууралуу',
  },
  {
    path: '/tariffs',
    icon: BadgePercent,
    key: 'tariffs',
    label: 'Тарифтер',
  },
  {
    path: '/terms',
    icon: FileText,
    key: 'terms',
    label: 'Колдонуу шарттары',
  },
  {
    path: '/privacy',
    icon: Lock,
    key: 'privacy',
    label: 'Купуялуулук саясаты',
  },
];

const SOCIALS = [
  {
    href: 'https://www.instagram.com/turaq.kg/',
    icon: FaInstagram,
    label: 'Instagram',
  },
  {
    href: 'https://www.facebook.com/profile.php?id=61563751618045',
    icon: FaFacebook,
    label: 'Facebook',
  },
  {
    href: 'https://t.me/turaq_kg',
    icon: Send,
    label: 'Telegram',
  },
  {
    href: 'https://www.tiktok.com/@turaq.kg',
    icon: FaTiktok,
    label: 'TikTok',
  },
  {
    href: 'https://turaq.kg/',
    icon: FaGlobe,
    label: 'Website',
  },
];

export function Footer() {
  const { t, i18n } = useTranslation();
  const lang = (SEO_LANGS.find((l) => l.code === i18n.language)?.code ?? 'ru') as LangCode;

  return (
    <footer className="relative mt-auto overflow-hidden text-white">
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(${FooterImgBG})`,
        }}
      />

      <div className="absolute inset-0 bg-gradient-to-r from-[#0d1b3e]/95 via-[#0d1b3e]/80 to-[#0d1b3e]/60" />

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8">
        {/* Top */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3">
              <div
                className="
      flex items-center justify-center
      w-14 h-14
      rounded-2xl
      bg-white/10
      border border-white/10
      backdrop-blur-sm
      overflow-hidden
      shadow-lg shadow-black/20
    "
              >
                <img
                  src={LogoTuraq}
                  alt="Turaq.kg"
                  width={40}
                  height={40}
                  loading="lazy"
                  decoding="async"
                  className="w-10 h-10 object-contain"
                />
              </div>

              <span className="text-2xl font-black tracking-wide">
                TURAQ.KG
              </span>
            </div>

            <p className="mt-5 text-sm leading-7 text-gray-300">
              {t(
                'footer.description',
                'Кыргызстандын алдыңкы турак-жай платформасы. 7 облус боюнча өзүңүзгө ылайыктуу турак-жайды табыңыз.',
              )}
            </p>

            <Link
              to="/create-listing"
              className="
                mt-6 inline-flex items-center gap-2
                rounded-xl bg-blue-600
                px-5 py-3
                text-sm font-semibold
                transition-all duration-300
                hover:bg-blue-500
                hover:scale-[1.02]
              "
            >
              <PlusSquare size={17} />

              {t('footer.links.post', 'Жарнама жайгаштыруу')}
            </Link>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="text-base font-bold">
              {t('footer.quickLinksTitle', 'Тез шилтемелер')}
            </h3>

            <div className="mt-3 mb-5 h-1 w-10 rounded-full bg-blue-500" />

            <ul className="space-y-3">
              {NAV_LINKS.map(({ path, icon: Icon, key, label }) => (
                <li key={key}>
                  <Link
                    to={path}
                    className="
                      group flex items-center gap-3
                      text-sm text-gray-300
                      transition-all duration-200
                      hover:text-white
                    "
                  >
                    <Icon
                      size={16}
                      className="
                        text-gray-500
                        transition-colors duration-200
                        group-hover:text-blue-400
                      "
                    />

                    {t(`footer.links.${key}`, label)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-base font-bold">
              {t('footer.companyTitle', 'Компания')}
            </h3>

            <div className="mt-3 mb-5 h-1 w-10 rounded-full bg-blue-500" />

            <ul className="space-y-3">
              {COMPANY_LINKS.filter((l) => l.path !== '/tariffs' || !isNativeApp()).map(({ path, icon: Icon, key, label }) => (
                <li key={key}>
                  <Link
                    to={path}
                    className="
                      group flex items-center gap-3
                      text-sm text-gray-300
                      transition-all duration-200
                      hover:text-white
                    "
                  >
                    <Icon
                      size={16}
                      className="
                        text-gray-500
                        transition-colors duration-200
                        group-hover:text-blue-400
                      "
                    />

                    {t(`footer.companyLinks.${key}`, label)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contacts */}
          <div>
            <h3 className="text-base font-bold">
              {t('footer.contactTitle', 'Байланыш')}
            </h3>

            <div className="mt-3 mb-5 h-1 w-10 rounded-full bg-blue-500" />

            <ul className="space-y-4 text-sm text-gray-300">
              <li>
                <a
                  href="tel:+996503699990"
                  className="flex items-center gap-3 hover:text-white transition-colors"
                >
                  <Phone size={16} className="text-gray-500" />
                  +996 503 699 990
                </a>
              </li>

              <li>
                <a
                  href="mailto:turaq.kg@gmail.com"
                  className="flex items-center gap-3 hover:text-white transition-colors"
                >
                  <Mail size={16} className="text-gray-500" />
                  turaq.kg@gmail.com
                </a>
              </li>

              <li className="flex items-center gap-3">
                <MapPin size={16} className="text-gray-500" />

                {t('footer.address', 'Бишкек, Кыргызстан')}
              </li>

              <li className="flex items-center gap-3">
                <Clock size={16} className="text-gray-500" />

                {t('footer.workingHours', 'Дүйшөмбү – Жекшемби: 09:00 – 18:00')}
              </li>
            </ul>
          </div>

          {/* Socials */}
          <div>
            <h3 className="text-base font-bold">
              {t('footer.followTitle', 'Бизди социалдык тармактардан байкаңыз')}
            </h3>

            <div className="mt-3 mb-5 h-1 w-10 rounded-full bg-blue-500" />

            <div className="flex flex-wrap gap-3">
              {SOCIALS.map(({ href, icon: Icon, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="
                    flex h-11 w-11 items-center justify-center
                    rounded-xl
                    border border-white/10
                    bg-white/5
                    transition-all duration-300
                    hover:scale-105
                    hover:border-blue-500
                    hover:bg-blue-600
                  "
                >
                  <Icon size={18} className="text-white" />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* SEO: real estate by city */}
        <div className="mt-10 border-t border-white/10 pt-8">
          <h3 className="text-sm font-bold text-gray-300 mb-3">
            {t('footer.citiesTitle', 'Недвижимость по городам')}
          </h3>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {SEO_REGIONS.map((r) => {
              const langDef = SEO_LANGS.find((l) => l.code === lang);
              return (
                <Link
                  key={r.slug}
                  to={hubPath(r.slug, undefined, undefined, langDef?.urlPrefix || undefined)}
                  className="text-sm text-gray-400 hover:text-white transition-colors"
                >
                  {r.names[lang]}
                </Link>
              );
            })}
          </div>
        </div>

        {/* Bottom */}
        <div
          className="
            mt-12
            flex flex-col items-center justify-between gap-4
            border-t border-white/10
            pt-6
            text-sm text-gray-400
            md:flex-row
          "
        >
          <div className="flex items-center gap-3">
            <div
              className="
                flex h-9 w-9 items-center justify-center
                rounded-full
                border border-blue-500/30
                bg-blue-500/10
              "
            >
              <ShieldCheck size={16} className="text-blue-400" />
            </div>
          </div>

          <p className="border-t border-white/10 mt-5 pt-6 pb-17 text-center text-gray-400 text-sm">
            © {new Date().getFullYear()}
            {t('footer.copyright', 'Все права защищены')}{' '}
          </p>
        </div>
      </div>
    </footer>
  );
}
